import CustomTable from "components/ReactTable/CustomTable";
import React, { useState, useEffect, useMemo, useContext } from "react";
import { Button, Container, Card, CardTitle } from "reactstrap";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { successToaster } from "components/common/toast";
import { ErrorToaster } from "components/common/toast";
import { DeleteUsersAdminAPI } from "network/user";
import { UserContextProvider } from "context/UserContext";
import { GetUsersAdminAPI } from "network/user";
import { GetUsersResellerAPI } from "network/user";
import { DeleteUsersResellerAPI } from "network/user";
import PATHS from "constant/paths";
import { useNavigate } from "react-router-dom";
import { GetResellerAPI } from "network/user";
import EditUserModalFrom from "components/modal/EditUserModalFrom";
import { moduleLabel } from "constant/modules";
import TableFilter from "components/TableFilter/TableFilter";
import { useTranslation } from "react-i18next";

const UserTable = () => {
  const { t } = useTranslation("users");
  const { user } = useContext(UserContextProvider);
  const [userData, setUserData] = useState([]);
  const [singleUser, setSingleUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [alert, setAlert] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [fetchTrigger, setFetchTrigger] = useState(false);

  const navigate = useNavigate();
  const pageSize = 10;

  const loadResellerOptions = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;

    try {
      const response = await GetResellerAPI(page, pageSize, search);

      if (response.success) {
        const newOptions = response.data.reseller.map((item) => ({
          value: item?.id,
          label: item?.first_name + " " + item?.last_name,
        }));
        return {
          options: [...newOptions],
          hasMore: response?.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }

    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };
  const fetchUser = async () => {
    try {
      setIsLoading(true);
      if (
        user?.role?.toLowerCase() === "admin" ||
        user?.role?.toLowerCase() === "super_admin"
      ) {
        const response = await GetUsersAdminAPI(
          currentPage,
          pageSize,
          search,
          sort?.value,
          role?.value
        );
        if (response.success) {
          setUserData(response?.data?.users);
          setIsLoading(false);
          setTotalPages(response?.data?.total_pages);
        }
      } else if (user?.role?.toLowerCase() === "reseller") {
        const response = await GetUsersResellerAPI(
          currentPage,
          pageSize,
          search,
          sort?.value
        );
        if (response.success) {
          setUserData(response?.data?.users);
          setIsLoading(false);
          setTotalPages(response?.data?.total_pages);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };
  useEffect(() => {
    setCurrentPage(1);
    setFetchTrigger((prev) => !prev);
  }, [sort, search, role]);

  useEffect(() => {
    fetchUser(currentPage);
  }, [currentPage, fetchTrigger]);

  const handleEdit = (items) => {
    setSingleUser(items);
    setOpen(true);
  };

  const warningWithConfirmMessage = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("table.confirmTitle")}
        onConfirm={() => successDelete(id)}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("table.confirmDelete")}
        cancelBtnText={t("table.cancel")}
        showCancel
        btnSize=""
      >
        {t("table.confirmText")}
      </ReactBSAlert>
    );
  };
  const hideAlert = () => {
    setAlert(null);
  };
  const successDelete = async (id) => {
    try {
      let response;
      if (
        user?.role?.toLowerCase() === "admin" ||
        user?.role?.toLowerCase() === "super_admin"
      ) {
        response = await DeleteUsersAdminAPI(id);
      } else if (user?.role?.toLowerCase() === "reseller") {
        response = await DeleteUsersResellerAPI(id);
      }

      if (response.success) {
        successToaster(t("table.deleted"));
        setUserData((prevUsers) =>
          prevUsers.filter((user) => user?.uuid !== id)
        );

        hideAlert();
      } else {
        hideAlert();
        ErrorToaster(t("table.deleteFailed"));
      }
    } catch (error) {
      hideAlert();
      ErrorToaster(t("table.deleteFailed"));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t("table.name"),
        accessor: "fullName",
        Cell: ({ row }) => {
          const first = row?.original?.first_name || "";
          const last = row?.original?.last_name || "";
          const fullName = `${first} ${last}`.trim();
          return fullName ? fullName : "-";
        },
      },
      {
        Header: t("table.email"),
        accessor: "email",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("table.phone"),
        accessor: "phone_number",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("table.role"),
        accessor: "role",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("table.modules"),
        accessor: "modules",
        Cell: ({ row }) =>
          row?.original?.role === "user" ? (
            <div className="modules-badges">
              {(row?.original?.modules || []).map((code) => (
                <span key={code} className="badge">
                  {moduleLabel(code)}
                </span>
              ))}
            </div>
          ) : (
            "-"
          ),
      },
      {
        Header: t("table.action"),
        accessor: "action",
        Cell: ({ row }) => (
          <div className="d-flex gap-3">
            <i
              className="tim-icons icon-pencil mr-3 text-warning pointer"
              onClick={() => handleEdit(row?.original)}
            ></i>
            <i
              className="tim-icons icon-trash-simple text-danger pointer"
              onClick={() => warningWithConfirmMessage(row?.original?.uuid)}
            ></i>
          </div>
        ),
      },
    ],
    [t]
  );

  return (
    <>
      <div className="content">
        {alert}
        <Container>
          <Card className="p-4">
            <div className="d-flex flex-wrap  justify-content-between align-items-center">
              <CardTitle className="mb-2" tag="h2">
                {t("table.title")}
              </CardTitle>
              <Button
                className="text-capitalize mb-2"
                color="primary"
                onClick={() => navigate(PATHS.ADD_USER)}
              >
                {t("table.addUser")}
              </Button>
            </div>
            <TableFilter
              search={search}
              setSearch={setSearch}
              sort={sort}
              setSort={setSort}
              showSearch
              showSort
              showRole={
                user?.role?.toLowerCase() === "admin" ||
                user?.role?.toLowerCase() === "super_admin"
                  ? true
                  : false
              }
              setRole={setRole}
              role={role}
            />
            <CustomTable
              columns={columns}
              data={userData || []}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              showPagination={totalPages > 1}
              isLoading={isLoading}
            />
          </Card>
        </Container>
        <EditUserModalFrom
          open={open}
          setOpen={setOpen}
          singleUser={singleUser}
          setUserData={setUserData}
          loadResellerOptions={loadResellerOptions}
        />
      </div>
    </>
  );
};

export default UserTable;
